import {ChangeDetectionStrategy, Component, computed, ElementRef, inject, Input} from '@angular/core';
import {CommonModule} from '@angular/common';
import {DonutChartComponent, DonutDatum, NgxColor} from '../donut--chart-card/donut-chart.component';
import {ThemeService} from '@/app/core/services/theme/theme.service';
import {getContrastingTextColor} from '../../../utils/color.utils';

type DonutColors = {
  primary: string;
  secondary: string;
  track?: string;
  labelPrimary?: string;
  labelSecondary?: string;
};

@Component({
  selector: 'app-donut-card',
  standalone: true,
  imports: [CommonModule, DonutChartComponent],
  templateUrl: './donut-card.component.html',
  styleUrls: ['./donut-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DonutCardComponent {
  @Input() title = '';
  @Input() rightHint?: string;
  @Input() primaryLabel = 'Evaluados';
  @Input() secondaryLabel = 'Sin evaluar';

  @Input() primaryCount = 0;
  @Input() totalCount = 0;

  @Input() colors: DonutColors = {
    primary: '#E99B38',
    secondary: '#CFCFCF',
    track: '#F2F2F2',
  };

  protected reportContent = computed(() => this.themeContent()?.reports);
  protected donutCardComponent = computed(() => this.reportContent()?.donutCardComponent);
  private themeService = inject(ThemeService);
  protected themeContent = this.themeService.themeContent;
  readonly firstSliceColor = computed(() => {
    const firstName = this.sortedData()[0]?.name;
    if (firstName === this.primaryLabel) {
      return this.normalizedColors.pri;
    }
    return this.normalizedColors.sec;
  });

  // === % y posiciones para overlay ===
  readonly cx = 100;
  readonly cy = 100;
  readonly r = 70;

  readonly primaryPct = computed(() => {
    const t = Number(this.totalCount) || 0;
    const p = Math.min(Number(this.primaryCount) || 0, t);
    return t ? Math.round((p / t) * 100) : 0;
  });
  readonly secondaryPct = computed(() => 100 - this.primaryPct());
  private readonly sortedData = computed<DonutDatum[]>(() => {
    const total = Number(this.totalCount) || 0;
    const primary = Math.max(0, Math.min(Number(this.primaryCount) || 0, total));
    const secondary = Math.max(0, total - primary);
    const data: DonutDatum[] = [
      {name: this.primaryLabel || 'Evaluados', value: primary},
      {name: this.secondaryLabel || 'Sin evaluar', value: secondary},
    ];
    return data.sort((a, b) => b.value - a.value);
  });
  readonly firstPct = computed(() => {
    const total = this.totalCount || 0;
    if (total === 0) return 0;
    const firstValue = this.sortedData()[0]?.value ?? 0;
    return Math.round((firstValue / total) * 100);
  });

  readonly secondPct = computed(() => {
    const total = this.totalCount || 0;
    if (total === 0) return 0;
    const secondValue = this.sortedData()[1]?.value ?? 0;
    return Math.round((secondValue / total) * 100);
  });

  // === Datos/colores para el chart  ===
  get normalizedColors() {
    return {
      pri: this.colors?.primary ?? '#E99B38',
      sec: this.colors?.secondary ?? '#CFCFCF',
    };
  }
  readonly secondSliceColor = computed(() => {
    const secondName = this.sortedData()[1]?.name;
    if (secondName === this.primaryLabel) {
      return this.normalizedColors.pri;
    }
    return this.normalizedColors.sec;
  });
  private elementRef = inject(ElementRef);
  readonly firstTextColor = computed(() => {
    return getContrastingTextColor(this.firstSliceColor(), this.elementRef.nativeElement);
  });

  readonly secondTextColor = computed(() => {
    return getContrastingTextColor(this.secondSliceColor(), this.elementRef.nativeElement);
  });

  get donutColors(): NgxColor[] {
    return [
      { name: this.primaryLabel || 'Evaluados', value: this.normalizedColors.pri },
      {name: this.secondaryLabel || 'Sin evaluar', value: this.normalizedColors.sec},
    ];
  }

  get donutData(): DonutDatum[] {
    return this.sortedData();
  }

  posFor(pct: number, startPct: number, radiusOffset = 0) {
    const ang = this.midAngle(pct, startPct) * (Math.PI / 180);
    const rr = this.r + radiusOffset;
    return {x: this.cx + rr * Math.cos(ang), y: this.cy + rr * Math.sin(ang)};
  }

  private midAngle(pct: number, startPct: number): number {
    const start = (startPct / 100) * 360 - 90;
    const sweep = (pct / 100) * 360;
    return start + sweep / 2;
  }
}
